import { default as BSNavbar } from 'react-bootstrap/Navbar'
import Nav from 'react-bootstrap/Nav'
import 'lib/reactBootstrapReact19/nav'
import { useRouter } from 'next/router'
import styles from './index.module.scss'
import NavLink from './components/NavLink'
import classNames from 'classnames'
import _omit from 'lodash/omit'
import LoggedOutNavItems from './LoggedOutNavItems'
import useThriveAuth from 'hooks/useThriveAuth'
import useBreakpoint from 'hooks/useBreakPoint'
import UserLoggedInNavItems from './UserLoggedInNavItems'
import SupportLoggedInNavItems from './SupportLoggedInNavItems'
import BrandLogoLink from 'common/BrandLogo/BrandLogoLink'
import { callRudderEventForBookingCTAclicked } from 'app/ProviderSearch/rudderEventUtils'
import { BookingButtonLocation } from 'app/Bookings/constants'
import B2BClientFilter from 'app/ProviderSearch/HeaderFilter/B2BClientFilter'
import useAppSelector from 'hooks/useAppSelector'
import { checkPathIsProviderProfileURL } from 'utils/common'
import { getSfAddress, getProviderSearchUrl } from 'hooks/salesforce/utils'
import useSalesforceLead from 'hooks/salesforce/useSalesforceLead'
import useSalesforceCase from 'hooks/salesforce/useSalesforceCase'
import useFetchLocation from 'hooks/useFetchLocation'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import ThirdPatyAgentLoggedInNavItems from './ThirdPartyAgentLoggedInNavItems'
import { isSeFriendlyRoute } from 'app/SeFriendly/helpers'
import { usedForFeature } from 'constants/logEvents'

type NavbarProps = {
  navbarContainerStyle?: string
  hideBookSessionButton?: boolean
  hideAuthNavItems?: boolean
  isDiagnosticHelper?: boolean
}

const Navbar: React.FC<NavbarProps> = ({
  children,
  hideBookSessionButton = false,
  hideAuthNavItems = false,
  navbarContainerStyle = styles.navbar,
  isDiagnosticHelper = false,
}) => {
  const router = useRouter()
  const { asPath } = router
  const { b2bUserDetails } =
    useAppSelector((state) => state.bookingHelper) ?? {}
  const { isB2BUser = false } = b2bUserDetails ?? {}
  const isProviderProfilePage = checkPathIsProviderProfileURL(asPath)
  const {
    isSupportUser,
    isPatient,
    isThirdPartyAgent,
    isAuthenticated,
    isLoading,
    isUserLoading,
  } = useThriveAuth()
  const { isMobileScreen } = useBreakpoint()

  const { lead, hasLeadId } = useSalesforceLead()
  const { sfCase, sfAccount, hasCaseId } = useSalesforceCase()
  const sfLocation =
    sfAccount?.PersonMailingAddress?.city ??
    sfAccount?.PersonMailingAddress?.state ??
    sfAccount?.PersonMailingAddress?.postalCode

  const { locationData } = useFetchLocation({
    locationString: sfLocation,
    fetchLocationEnabled: !!sfLocation,
    usedForFeature: usedForFeature.NavbarLocationFromSalesForce,
  })

  const sfAccountAddress = getSfAddress(sfAccount, locationData)

  const sfCaseAutoFill =
    useGetTreatment(TreatmentFlags.SALESFORCE_CASE_AUTOFILL) ===
    treatmentFlagValues.ON

  if (isLoading || isUserLoading) {
    return null
  }

  const isSeFriendly = isSeFriendlyRoute(router.pathname, router.asPath)

  const providerSearchUrl = getProviderSearchUrl(
    hasLeadId,
    lead,
    hasCaseId,
    sfCase,
    sfAccountAddress,
    sfCaseAutoFill
  )

  return (
    <>
      <BSNavbar
        className={classNames(
          navbarContainerStyle,
          'bg-white flex-nowrap font-gibson'
        )}
        expand="lg"
      >
        <BSNavbar.Brand>
          <BrandLogoLink
            width={isMobileScreen ? 114 : 225}
            height={isMobileScreen ? 25 : 49}
            className="c-pointer"
          />
        </BSNavbar.Brand>

        {/*--------------------------------Navbar for Mobile and Desktop--------------------------------*/}
        {!isSeFriendly && (
          <BSNavbar.Collapse id="basic-navbar-nav">
            <Nav className="flex-fill">{children}</Nav>
          </BSNavbar.Collapse>
        )}
        {/*--------------------------------Navbar for Mobile--------------------------------*/}
        {isSeFriendly && (
          <div className={styles.seFriendly_form_search_container}>
            {children}
          </div>
        )}

        {isProviderProfilePage && isB2BUser && isSupportUser && (
          <B2BClientFilter
            b2bButtonClass={styles.profile_b2b_filter_btn}
            containerClass={styles.profile_b2b_btn_container}
          />
        )}
        <div className={styles.nav_action_button_container}>
          {!hideBookSessionButton && !isDiagnosticHelper && (
            <NavLink href={providerSearchUrl}>
              <button
                className={classNames(styles.book_session_button)}
                onClick={(event) =>
                  callRudderEventForBookingCTAclicked(
                    BookingButtonLocation.TOP_NAVIGATION_BAR,
                    (event.target as HTMLInputElement).className,
                    (event.target as HTMLInputElement).innerText
                  )
                }
              >
                Find a provider
              </button>
            </NavLink>
          )}
        </div>
        {!hideAuthNavItems && (
          <div className="zindex-dropdown">
            {/* -------------------Logged in Support user Navbar------------ */}
            {isAuthenticated && isSupportUser && <SupportLoggedInNavItems />}

            {/* -------------------Logged in Third party agent user Navbar------------ */}
            {isAuthenticated && isThirdPartyAgent && (
              <ThirdPatyAgentLoggedInNavItems />
            )}

            {/* -------------------Logged in Normal user Navbar------------- */}
            {isAuthenticated && isPatient && <UserLoggedInNavItems />}

            {/* -------------------Logged out user Navbar------------------- */}
            {!isAuthenticated && <LoggedOutNavItems />}
          </div>
        )}
      </BSNavbar>
      {isSeFriendly && (
        <div className={styles.seFriendly_mobile_form_search_container}>
          {children}
        </div>
      )}
    </>
  )
}

export default Navbar
